const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';

const distance2D = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const lerp = (a, b, t) => a + (b - a) * t;

export class HandInputController {
  constructor(options) {
    this.video = options.video;
    this.overlay = options.overlay;
    this.cursor = options.cursor;
    this.viewport = options.viewport;
    this.onPointerDown = options.onPointerDown ?? (() => false);
    this.onPointerMove = options.onPointerMove ?? (() => {});
    this.onPointerUp = options.onPointerUp ?? (() => {});
    this.onState = options.onState ?? (() => {});
    this.onStatus = options.onStatus ?? (() => {});

    this.context = this.overlay.getContext('2d');
    this.landmarker = null;
    this.visionModule = null;
    this.HandLandmarker = null;
    this.stream = null;
    this.running = false;
    this.loading = false;
    this.animationFrame = null;
    this.lastVideoTime = -1;
    this.lastDetectionAt = 0;
    this.lastHandSeenAt = 0;
    this.cursorX = 0;
    this.cursorY = 0;
    this.cursorInitialized = false;
    this.pinching = false;
    this.dragAccepted = false;
    this.detectInterval = 50;

  }

  async start() {
    if (this.running || this.loading) return;
    this.loading = true;
    this.onState({ state: 'loading', label: 'загрузка' });

    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Браузер не предоставляет доступ к камере через getUserMedia.');
      }

      if (!window.isSecureContext && location.hostname !== 'localhost' && location.hostname !== '127.0.0.1') {
        throw new Error('Камера работает только через HTTPS или localhost.');
      }

      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: false,
        video: {
          facingMode: 'user',
          width: { ideal: 960 },
          height: { ideal: 720 },
          frameRate: { ideal: 30, max: 30 },
        },
      });

      this.video.srcObject = this.stream;
      await this.video.play();
      this.#resizeOverlay();

      if (!this.visionModule) {
        this.visionModule = await import('@mediapipe/tasks-vision');
        this.HandLandmarker = this.visionModule.HandLandmarker;
      }

      const base = import.meta.env.BASE_URL || './';
      const vision = await this.visionModule.FilesetResolver.forVisionTasks(`${base}wasm`);
      this.landmarker = await this.#createLandmarker(vision);

      this.running = true;
      this.loading = false;
      this.lastVideoTime = -1;
      this.lastDetectionAt = 0;
      this.onState({ state: 'running', label: 'активна' });
      this.onStatus('Камера активна. Покажите одну руку и соедините большой и указательный пальцы.');
      this.animationFrame = requestAnimationFrame((time) => this.#loop(time));
    } catch (error) {
      this.loading = false;
      this.#stopStream();
      this.onState({ state: 'error', label: 'ошибка', error });
      throw error;
    }
  }

  async #createLandmarker(vision) {
    const common = {
      runningMode: 'VIDEO',
      numHands: 1,
      minHandDetectionConfidence: 0.55,
      minHandPresenceConfidence: 0.55,
      minTrackingConfidence: 0.5,
    };

    try {
      return await this.HandLandmarker.createFromOptions(vision, {
        ...common,
        baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
      });
    } catch (gpuError) {
      this.onStatus('GPU-режим недоступен; используется CPU.');
      try {
        return await this.HandLandmarker.createFromOptions(vision, {
          ...common,
          baseOptions: { modelAssetPath: MODEL_URL, delegate: 'CPU' },
        });
      } catch (cpuError) {
        cpuError.cause = gpuError;
        throw cpuError;
      }
    }
  }

  stop() {
    if (!this.running && !this.loading && !this.stream) return;
    this.running = false;
    this.loading = false;
    cancelAnimationFrame(this.animationFrame);
    this.animationFrame = null;
    this.#releasePinch();
    this.cursor.classList.remove('is-visible', 'is-pinching');
    this.context.clearRect(0, 0, this.overlay.width, this.overlay.height);
    this.landmarker?.close?.();
    this.landmarker = null;
    this.#stopStream();
    this.onState({ state: 'stopped', label: 'выключена' });
    this.onStatus('Управление камерой выключено.');
  }

  async toggle() {
    if (this.running || this.loading) {
      this.stop();
      return false;
    }
    await this.start();
    return true;
  }

  #stopStream() {
    if (this.stream) {
      for (const track of this.stream.getTracks()) track.stop();
    }
    this.stream = null;
    this.video.srcObject = null;
  }

  #loop(timestamp) {
    if (!this.running) return;

    if (
      this.video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA &&
      this.video.currentTime !== this.lastVideoTime &&
      timestamp - this.lastDetectionAt >= this.detectInterval
    ) {
      this.lastVideoTime = this.video.currentTime;
      this.lastDetectionAt = timestamp;

      try {
        const result = this.landmarker.detectForVideo(this.video, timestamp);
        this.#processResult(result, timestamp);
      } catch (error) {
        this.onStatus(`Ошибка распознавания руки: ${error.message || error}`);
      }
    }

    if (timestamp - this.lastHandSeenAt > 260) {
      this.#handleMissingHand();
    }

    this.animationFrame = requestAnimationFrame((time) => this.#loop(time));
  }

  #processResult(result, timestamp) {
    const landmarks = result.landmarks?.[0];
    this.#drawLandmarks(landmarks);

    if (!landmarks?.length) return;
    this.lastHandSeenAt = timestamp;

    const indexTip = landmarks[8];
    const thumbTip = landmarks[4];
    const indexBase = landmarks[5];
    const pinkyBase = landmarks[17];
    const palmWidth = Math.max(0.025, distance2D(indexBase, pinkyBase));
    const pinchRatio = distance2D(indexTip, thumbTip) / palmWidth;

    const rect = this.viewport.getBoundingClientRect();
    const targetX = rect.left + (1 - indexTip.x) * rect.width;
    const targetY = rect.top + indexTip.y * rect.height;

    if (!this.cursorInitialized) {
      this.cursorX = targetX;
      this.cursorY = targetY;
      this.cursorInitialized = true;
    } else {
      this.cursorX = lerp(this.cursorX, targetX, 0.38);
      this.cursorY = lerp(this.cursorY, targetY, 0.38);
    }

    this.cursor.style.left = `${this.cursorX}px`;
    this.cursor.style.top = `${this.cursorY}px`;
    this.cursor.classList.add('is-visible');

    const shouldStartPinch = !this.pinching && pinchRatio < 0.43;
    const shouldEndPinch = this.pinching && pinchRatio > 0.61;

    if (shouldStartPinch) {
      this.pinching = true;
      this.cursor.classList.add('is-pinching');
      this.dragAccepted = Boolean(this.onPointerDown(this.cursorX, this.cursorY, 'hand'));
      this.onState({ state: 'running', label: this.dragAccepted ? 'pinch' : 'активна', pinchRatio });
    } else if (shouldEndPinch) {
      this.#releasePinch();
      this.onState({ state: 'running', label: 'активна', pinchRatio });
    } else if (this.pinching && this.dragAccepted) {
      this.onPointerMove(this.cursorX, this.cursorY, 'hand');
    }
  }

  #releasePinch() {
    if (this.pinching && this.dragAccepted) {
      this.onPointerUp(this.cursorX, this.cursorY, 'hand');
    }
    this.pinching = false;
    this.dragAccepted = false;
    this.cursor.classList.remove('is-pinching');
  }

  #handleMissingHand() {
    this.#releasePinch();
    this.cursor.classList.remove('is-visible');
    this.cursorInitialized = false;
    this.context.clearRect(0, 0, this.overlay.width, this.overlay.height);
  }

  #resizeOverlay() {
    const width = this.video.videoWidth || 960;
    const height = this.video.videoHeight || 720;
    this.overlay.width = width;
    this.overlay.height = height;
  }

  #drawLandmarks(landmarks) {
    const ctx = this.context;
    const width = this.overlay.width;
    const height = this.overlay.height;
    ctx.clearRect(0, 0, width, height);
    if (!landmarks?.length) return;

    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = 'rgba(200, 255, 114, 0.72)';
    ctx.lineWidth = Math.max(2, width / 360);

    for (const connection of this.HandLandmarker.HAND_CONNECTIONS) {
      const start = landmarks[connection.start];
      const end = landmarks[connection.end];
      ctx.beginPath();
      ctx.moveTo(start.x * width, start.y * height);
      ctx.lineTo(end.x * width, end.y * height);
      ctx.stroke();
    }

    for (let index = 0; index < landmarks.length; index += 1) {
      const point = landmarks[index];
      const isTip = [4, 8, 12, 16, 20].includes(index);
      ctx.beginPath();
      ctx.arc(point.x * width, point.y * height, isTip ? width / 105 : width / 155, 0, Math.PI * 2);
      ctx.fillStyle = isTip ? 'rgba(242, 244, 236, 0.96)' : 'rgba(200, 255, 114, 0.88)';
      ctx.fill();
    }
  }
}
